/* eslint-disable @typescript-eslint/no-empty-function */
import React from 'react';
import {
  useProductSwapInitializer,
  ProductSwapOptions,
} from './useProductContext';
import { ProductSwapTypes } from '../../utils/constants';

const EMPTY: unique symbol = Symbol('EMPTY');

export interface ProductContext extends ProductSwapOptions {
  product: Product;
  isDecreaseDisabled: boolean | ((quantity: number) => boolean);
  updateBundle: (
    newBundleProps,
    lineId: string,
    dynamicBabItems?: unknown,
    sellingPlan?: string | number,
    discountCodes?: Discount[],
    oneTimeAddon?: boolean,
  ) => void;
  updateVariant: (
    updateVariantLine: GlobalVariant & { quantity: number },
    line: string,
  ) => void;
  updateProduct: (
    productId: string,
    quantity: number,
    lineId: string,
    updateProduct: GlobalVariant,
  ) => void;
  swapProductForBundle: (
    newBundleProps,
    lineId: string,
    dynamicBabItems: { id: string; quantity: number }[],
    sellingPlan?: SellingPlan,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
  ) => void;
  removeProduct: (lineId: string) => void;
  setShowEditMyBoxModal: (showEditMyBoxModal: boolean) => void;
  setShowCurateShuffleModal: (showCurateShuffleModal: boolean) => void;
  isSlidePanelOpen: boolean;
  closeSlidePanel: () => void;
  openSlidePanel: (
    type: ProductSwapTypes,
    productId: string,
    variantId: string,
  ) => void;
  productSlidePanel: {
    show: boolean;
    product: ProductSwapTypes | null;
    productId: string;
    variantId: string;
  };
  image: string;
  sellingPlans: any[] | null;
  subscriptionContainsGift: boolean;
}

const ProductContext = React.createContext<ProductContext | typeof EMPTY>(
  EMPTY,
);
ProductContext.displayName = 'ProductContext';

const useProductContext = (): ProductContext | typeof EMPTY => {
  const context = React.useContext(ProductContext);
  if (context === EMPTY) {
    throw new Error('useProductContext must be used within a ProductProvider');
  }
  return context;
};

interface ProductProviderProps {
  children: React.ReactNode | React.ReactNode[];
  product: Product;
  isDecreaseDisabled: boolean | ((quantity: number) => boolean);
  updateBundle: (
    newBundleProps,
    lineId: string,
    dynamicBabItems?: unknown,
    sellingPlan?: string | number,
    discountCodes?: Discount[],
    oneTimeAddon?: boolean,
  ) => void;
  swapProductForBundle: (
    newBundleProps,
    lineId: string,
    dynamicBabItems: { id: string; quantity: number }[],
    sellingPlan: SellingPlan,
    discountCodes: string[],
    oneTimeAddon?: boolean,
  ) => void;
  updateVariant: (
    updateVariantLine: GlobalVariant & { quantity: number },
    line: string,
  ) => void;
  updateProduct: (
    productId: string,
    quantity: number,
    lineId: string,
    updateProduct: GlobalVariant,
  ) => void;
  removeProduct: (lineId: string) => void;
  setShowEditMyBoxModal: (showEditMyBoxModal: boolean) => void;
  setShowCurateShuffleModal: (showCurateShuffleModal: boolean) => void;
  sellingPlans: any[] | null;
  closeSlidePanel: () => void;
  openSlidePanel: (
    type: ProductSwapTypes,
    productId: string,
    variantId: string,
  ) => void;
  productSlidePanel: {
    show: boolean;
    product: ProductSwapTypes | null;
    productId: string;
    variantId: string;
    isFreeProduct: boolean;
  };
  image: string;
  isLastLine?: boolean;
  subscriptionContainsGift: boolean;
}

export const ProductProvider = ({
  children,
  product,
  image,
  sellingPlans,
  updateVariant,
  updateBundle,
  swapProductForBundle,
  removeProduct,
  setShowEditMyBoxModal,
  setShowCurateShuffleModal,
  closeSlidePanel,
  openSlidePanel,
  productSlidePanel,
  updateProduct,
  isDecreaseDisabled,
  isLastLine,
  subscriptionContainsGift,
}: ProductProviderProps): React.ReactElement => {
  const { menuOptions, productSwapModal, openModal, closeModal } =
    useProductSwapInitializer(
      product,
      removeProduct,
      setShowEditMyBoxModal,
      setShowCurateShuffleModal,
      openSlidePanel,
      closeSlidePanel,
      isLastLine,
    );

  const isSlidePanelOpen =
    productSlidePanel.show &&
    productSlidePanel.productId === product.ProductId &&
    productSlidePanel.variantId === product.VariantId;

  return (
    <ProductContext.Provider
      value={{
        product,
        image,
        sellingPlans,
        updateVariant,
        updateBundle,
        swapProductForBundle,
        removeProduct,
        setShowEditMyBoxModal,
        setShowCurateShuffleModal,
        closeSlidePanel,
        isSlidePanelOpen,
        openSlidePanel,
        productSlidePanel,
        updateProduct,
        menuOptions,
        productSwapModal,
        openModal,
        closeModal,
        isDecreaseDisabled,
        subscriptionContainsGift,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export default useProductContext;
